import type { Lesson } from '@aurea/contracts';
import { Button, Seg, Select } from '@aurea/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CalendarPlus, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Calendar } from '../../components/Calendar';
import { CalendarFeedDialog } from '../../components/CalendarFeedDialog';
import { LessonDialog } from '../../components/LessonDialog';
import { type LessonDraft, LessonForm } from '../../components/LessonForm';
import { MonthGrid } from '../../components/MonthGrid';
import { api } from '../../lib/api';
import {
  addDays,
  dayStart,
  fmtDayLong,
  fmtRange,
  hhmm,
  type LocalDay,
  localDay,
  sameDay,
  weekStart,
} from '../../lib/time';
import { canManage, isAdmin, useMe } from '../_app';

const Search = z.object({
  view: z.enum(['week', 'day', 'month']).optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  teacher: z.uuid().optional(),
});

export const Route = createFileRoute('/_app/schedule')({ validateSearch: Search, component: Schedule });

const toIso = (d: LocalDay) => `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
const fromIso = (s: string): LocalDay => {
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  return { y, m, d };
};

function Schedule() {
  const me = useMe();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tz = me.timezone;
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const staff = me.roles.some((r) => r === 'admin' || r === 'teacher');
  const requested = search.view ?? 'week';
  const view = staff || requested !== 'day' ? requested : 'week';
  const day = search.date ? fromIso(search.date) : localDay(new Date(), tz);
  const ws = weekStart(day);
  // Месяц — шесть недель, начиная с понедельника недели, где первое число.
  const gridStart = weekStart({ y: day.y, m: day.m, d: 1 });
  const from = dayStart(view === 'month' ? gridStart : ws, tz).toISOString();
  const to = dayStart(view === 'month' ? addDays(gridStart, 42) : addDays(ws, 7), tz).toISOString();
  const queryKey = ['lessons', from, to] as const;

  const lessons = useQuery({ queryKey, queryFn: () => api.lessons(from, to), placeholderData: (p) => p });
  const teachers = useQuery({
    queryKey: ['teachers'],
    queryFn: api.teachers,
    enabled: staff,
    staleTime: 5 * 60_000,
  });
  const [open, setOpen] = useState<Lesson | null>(null);
  const [draft, setDraft] = useState<LessonDraft | null>(null);
  const [feed, setFeed] = useState(false);

  const filtered = useMemo(
    () => (lessons.data ?? []).filter((l) => !search.teacher || l.teacher.id === search.teacher),
    [lessons.data, search.teacher],
  );
  // Учителя, у которых есть занятия в этот день, идут первыми.
  // biome-ignore lint/correctness/useExhaustiveDependencies: day вычисляется из search.date
  const teacherCols = useMemo(() => {
    const list = teachers.data ?? [];
    const busy = new Set(
      (lessons.data ?? [])
        .filter((l) => sameDay(localDay(new Date(l.startsAt), tz), day))
        .map((l) => l.teacher.id),
    );
    return [...list].sort((a, b) => Number(busy.has(b.id)) - Number(busy.has(a.id)));
  }, [teachers.data, lessons.data, search.date, tz]);

  const go = (patch: Partial<z.infer<typeof Search>>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const shift = (dir: -1 | 1) =>
    go({
      date:
        view === 'month'
          ? toIso(
              day.m + dir < 1
                ? { y: day.y - 1, m: 12, d: 1 }
                : day.m + dir > 12
                  ? { y: day.y + 1, m: 1, d: 1 }
                  : { y: day.y, m: day.m + dir, d: 1 },
            )
          : toIso(addDays(day, view === 'day' ? dir : dir * 7)),
    });
  const monthTitle = (() => {
    const s = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(
      new Date(Date.UTC(day.y, day.m - 1, 1)),
    );
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollHint, setScrollHint] = useState(false);
  useEffect(() => {
    const el = scrollRef.current?.querySelector('.cal-scroll');
    setScrollHint(view === 'day' && !!el && el.scrollWidth > el.clientWidth + 4);
  });

  return (
    <>
      <header className="page-head">
        <div>
          <h1>{t('schedule.title')}</h1>
        </div>
        <div className="flex-1" />
        <Button variant="ghost" onClick={() => setFeed(true)} aria-label={t('feed.title')}>
          <CalendarPlus className="i" />
          <span className="hide-mobile">{t('feed.button')}</span>
        </Button>
        {canManage(me) && (
          <Button onClick={() => setDraft({ date: toIso(day) })} aria-label={t('today.newLesson')}>
            <Plus className="i" />
            <span className="hide-mobile">{t('today.newLesson')}</span>
          </Button>
        )}
      </header>
      <section className="tray overflow-hidden" ref={scrollRef}>
        <div className="cal-bar">
          <h2 className="mr-auto text-xl font-semibold">
            {view === 'day'
              ? fmtDayLong(day, lang)
              : view === 'month'
                ? monthTitle
                : fmtRange(ws, addDays(ws, 6), lang)}
          </h2>
          {staff && (teachers.data?.length ?? 0) > 1 && (
            <Select
              pill
              value={search.teacher ?? ''}
              onChange={(e) => go({ teacher: e.target.value || undefined })}
              aria-label={t('schedule.teacher')}
            >
              <option value="">{t('schedule.allTeachers')}</option>
              {teachers.data?.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.firstName} {x.lastName}
                </option>
              ))}
            </Select>
          )}
          <Seg
            label={t('schedule.title')}
            value={view}
            onChange={(v) => go({ view: v })}
            options={[
              ...(staff ? [{ value: 'day' as const, label: t('schedule.day') }] : []),
              { value: 'week' as const, label: t('schedule.week') },
              { value: 'month' as const, label: t('schedule.month') },
            ]}
          />
          <Button variant="ghost" onClick={() => go({ date: undefined })}>
            {t('schedule.today')}
          </Button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => shift(-1)}
            aria-label={t('schedule.prev')}
          >
            <ChevronLeft className="i" />
          </button>
          <button type="button" className="icon-btn" onClick={() => shift(1)} aria-label={t('schedule.next')}>
            <ChevronRight className="i" />
          </button>
          {scrollHint && (
            <span className="w-full text-xs text-ink-3">
              {t('schedule.scrollHint', { teachers: t('plural.teachers', { count: teacherCols.length }) })}
            </span>
          )}
        </div>
        {lessons.isPending ? (
          <div className="skeleton m-6 h-[480px]" />
        ) : view === 'month' ? (
          <MonthGrid
            month={day.m}
            gridStart={gridStart}
            lessons={filtered}
            tz={tz}
            onOpenDay={(d) => go({ view: staff ? 'day' : 'week', date: toIso(d) })}
          />
        ) : (
          <Calendar
            view={view}
            weekStart={ws}
            day={day}
            lessons={filtered}
            teachers={teacherCols}
            teacherFilter={search.teacher}
            tz={tz}
            canEdit={canManage(me)}
            queryKey={queryKey}
            onOpenDay={(d) => go({ view: 'day', date: toIso(d) })}
            onOpenLesson={(l) => staff && setOpen(l)}
            onCreateAt={(d, min, teacherId) => setDraft({ date: toIso(d), time: hhmm(min), teacherId })}
          />
        )}
      </section>
      <LessonDialog
        lessonId={open?.id ?? null}
        tz={tz}
        canCancel={canManage(me)}
        onClose={() => setOpen(null)}
      />
      <LessonForm draft={draft} tz={tz} onClose={() => setDraft(null)} />
      {feed && <CalendarFeedDialog onClose={() => setFeed(false)} />}
    </>
  );
}
